import React, { Fragment, forwardRef, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Currency from "components/currency";
import styles from "./rate_plan_price_breakdown.module.css";
import { InfoCircleOutlined, InfoOutlined } from "@ant-design/icons";
import { Overlay } from "react-bootstrap";
import moment from "moment";

const DECIMAL_PLACES = 2;

function RatePlanPriceBreakdown(
  { ratePlan, currency, totalPrice, discountData, checkinDate, checkoutDate },
  ref,
) {
  const { t } = useTranslation();
  const { netPrice, lengthOfStay, taxes, rates } = ratePlan;
  const overlayTarget = useRef();
  const [showPriceInfo, setShowPriceInfo] = useState();
  function getDatesFromCheckInToCheckOut(checkIn, checkOut) {
    const startDate = moment(checkIn);
    const endDate = moment(checkOut);
    const dates = [];

    while (startDate.isBefore(endDate)) {
      dates.push(startDate.format("ddd DD MMM")); // Adjust format as needed
      startDate.add(1, "days");
    }

    return dates;
  }
  const daysBooked = useMemo(() => {
    return getDatesFromCheckInToCheckOut(checkinDate, checkoutDate);
  }, [checkinDate, checkoutDate]);

  const perNightPrice = (Number(netPrice) / lengthOfStay).toFixed(DECIMAL_PLACES);

  return (
    <div className={styles.container} ref={ref}>
      <div className={styles.left}>
        <Overlay target={overlayTarget.current} show={showPriceInfo} placement="right">
          {({ placement, arrowProps, show: _show, popper, ...props }) => (
            <div
              {...props}
              className={styles.overlay_container}
              style={{
                ...props.style,
              }}
            >
              <div className={styles.overlay_arrow} />
              <div className={styles.list_cont}>
                {daysBooked?.map(
                  (day, index) =>
                    rates?.[index] && (
                      <div
                        className="d-flex justify-content-between align-items-center"
                        key={index}
                      >
                        <div style={{ fontSize: 12 }}>{day}</div>
                        <div style={{ fontSize: 12 }}>{rates[index]}</div>
                      </div>
                    ),
                )}
              </div>
            </div>
          )}
        </Overlay>

        <div ref={overlayTarget} className="d-flex align-items-center pr-3">
          <div>{`${lengthOfStay} ${t("rates_table:nights")}`}</div>

          <div
            onClick={() => setShowPriceInfo(!showPriceInfo)}
            className="ml-1 d-flex align-items-center"
          >
            <span style={{ fontSize: 10 }}>Price Breakdown</span>{" "}
            <InfoCircleOutlined style={{ fontSize: 10, marginLeft: 3 }} />
          </div>
          {/* <Currency currency={currency} amount={netPrice} /> */}
        </div>
        {taxes.map((tax, index) => {
          if (!tax?.inclusive) {
            return <Fragment key={tax?.title}></Fragment>;
          }
          return (
            <div className={styles.priceRow} key={tax?.title}>
              <span>{tax?.title}</span>
            </div>
          );
        })}

        <div className="mt-1">
          <span className={styles.totalLabel}>{t("rates_table:total_price")}</span>
        </div>
        {discountData?.discountSequences?.map((ds, index) => (
          <div className={styles.priceRow} key={index}>
            <span>
              {ds?.promotion?.name} ({ds?.promotion?.discount}%)
            </span>
          </div>
        ))}

        {discountData?.discountSequences?.length > 0 && (
          <div className="mt-1">
            <span className={styles.totalLabel}>{t("rates_table:discounted_price")}</span>
          </div>
        )}
      </div>

      <div className={styles.right}>
        <Currency currency={currency} amount={netPrice} />
        {taxes.map((tax, index) => {
          if (!tax?.inclusive) {
            return <Fragment key={index}></Fragment>;
          }
          return (
            <div className={styles.priceRow} key={tax.title}>
              <Currency currency={currency} amount={tax.amount} />
            </div>
          );
        })}

        <div className="mt-1">
          <Currency className={styles.totalPrice} currency={currency} amount={totalPrice} />
        </div>
        {discountData?.discountSequences?.map((ds, index) => (
          <div className={styles.priceRow} key={index}>
            <Currency currency={currency} amount={-ds?.discountAmount} />
          </div>
        ))}
        {discountData?.discountSequences?.length > 0 && (
          <div className="mt-1">
            <Currency
              className={styles.totalPrice}
              currency={currency}
              amount={discountData?.finalPrice}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default forwardRef(RatePlanPriceBreakdown);
